// apps/web/src/store/badgesSlice.ts
// ─────────────────────────────────────────────────────────────────────────
// BADGES slice — reactive mirror of earned badges + a badge toast queue.
//
// Badges are permanent proof. This slice never grants Gems or XP (Rule 3).
// It holds:
//   • earned: badgeId → EarnedBadge (hydrated from badgeStore on boot)
//   • toastQueue: freshly-earned badges to celebrate (small vs major)
//   • bootBaseline: the set of badge ids the app could already PROVE at boot
//     (the result of the silent retroactive sweep). Anything in this set is
//     history, not a live achievement, and must NEVER live-toast — even if a
//     later live evaluation re-discovers it as "fresh" because of a boot race.
//
// The single evaluation entry point is the `evaluateBadgesThunk`. It builds a
// facts snapshot, runs the pure evaluator, persists any newly-earned ids, and
// enqueues celebration toasts for the fresh ones. Call it:
//   • once on boot with { retroactive: true }  → silent retro sweep. MUST be
//     awaited before the app becomes interactive so the baseline is complete.
//   • after meaningful actions with { retroactive: false } → may toast, but
//     only for badges that were NOT already earnable at boot.
//
// Why the baseline matters: most retroactive badges are gated by the reward
// ledger, so the event that flips the fact is the same event that runs the
// live eval — they toast exactly once, correctly. But a few badges are
// satisfied by AMBIENT durable state that is not itself an event
// (hasStoredFiNumber → 'found_your_number'; bestStreakDays → streak badges;
// level → level badges). Those become true at boot, decoupled from any reward
// event. Without the baseline, the first unrelated live eval "discovers" them
// and toasts them on the wrong screen (e.g. the "Calculated your FI number"
// toast popping when navigating back to Learn). The baseline makes that
// structurally impossible without special-casing individual predicates.
// ─────────────────────────────────────────────────────────────────────────

import {
	createSlice,
	createAsyncThunk,
	type PayloadAction,
} from '@reduxjs/toolkit';
import {
	getBadge,
	evaluateBadges,
	type EarnedBadge,
	type BadgeDef,
} from '@nisk/core/badges';
import { gatherBadgeFacts } from '../lib/badges/badgeFacts';
import { readEarnedBadges, persistNewlyEarned } from '../lib/badges/badgeStore';
import type { WithXp } from './xpSlice';

// The active release gate. Flip to 'v1.1' to promote held badges.
const ACTIVE_RELEASE: 'v1' | 'v1.1' = 'v1';

// ── Toast item ────────────────────────────────────────────────────────────
export interface BadgeToastItem {
	id: string;
	badgeId: string;
	name: string;
	description: string;
	/** 'major' badges get a bigger moment; 'small' a normal toast. */
	weight: 'small' | 'major';
	nextAction?: string;
}

// ── State ─────────────────────────────────────────────────────────────────
export interface BadgesState {
	earned: Record<string, EarnedBadge>;
	toastQueue: BadgeToastItem[];
	initialized: boolean;
	/**
	 * Badge ids that were already earnable at boot (post retroactive sweep).
	 * These are history and are suppressed from all live toasts. Empty until
	 * the first retroactive evaluation fulfills.
	 */
	bootBaseline: string[];
	/** True once the retroactive boot sweep has completed at least once. */
	baselineReady: boolean;
}

const initialState: BadgesState = {
	earned: {},
	toastQueue: [],
	initialized: false,
	bootBaseline: [],
	baselineReady: false,
};

function nextToastId(): string {
	return `badge-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function toToast(def: BadgeDef): BadgeToastItem {
	return {
		id: nextToastId(),
		badgeId: def.id,
		name: def.name,
		description: def.description,
		weight: def.celebration === 'major' ? 'major' : 'small',
		nextAction: def.nextAction,
	};
}

// ── Evaluation thunk (single entry point) ────────────────────────────────
export interface EvaluateArgs {
	/** true = silent retroactive sweep; false = live, may celebrate. */
	retroactive: boolean;
	/**
	 * Suppress toasts even for a live evaluation (e.g. when a result screen
	 * will surface the badge itself). The badge is still earned + persisted.
	 */
	silent?: boolean;
}

export const evaluateBadgesThunk = createAsyncThunk<
	{
		retroactive: boolean;
		/** All ids satisfied this evaluation (used to seed the baseline). */
		earnedIds: string[];
		/** Ids newly persisted this call. */
		freshIds: string[];
		/** Toasts to enqueue (already filtered against the boot baseline). */
		toasts: BadgeToastItem[];
	},
	EvaluateArgs,
	{ state: WithXp & { badges: BadgesState } }
>('badges/evaluate', async (args, { getState }) => {
	const state = getState();
	const level = state.xp.level;
	const facts = gatherBadgeFacts({ level });

	const { earnedIds } = evaluateBadges(facts, ACTIVE_RELEASE);
	const fresh = persistNewlyEarned(earnedIds, {
		retroactive: args.retroactive,
	});

	// Retroactive sweeps never toast. Live evals toast unless silenced — and
	// never for badges that were already earnable at boot (the baseline). The
	// baseline guard is what prevents ambient-state badges (FI number, streak,
	// level) from being celebrated by an unrelated live event after a boot
	// race. If the baseline isn't ready yet (a live event somehow beat the
	// awaited boot sweep), suppress live toasts entirely — better a missed
	// celebration than a false one on the wrong screen.
	const baseline = new Set(state.badges.bootBaseline);
	const shouldToast =
		!args.retroactive && !args.silent && state.badges.baselineReady;

	const toasts: BadgeToastItem[] = [];
	if (shouldToast) {
		for (const rec of fresh) {
			if (baseline.has(rec.badgeId)) continue; // history, not live
			const def = getBadge(rec.badgeId);
			if (def) toasts.push(toToast(def));
		}
	}

	return {
		retroactive: args.retroactive,
		earnedIds,
		freshIds: fresh.map((f) => f.badgeId),
		toasts,
	};
});

// ── Slice ─────────────────────────────────────────────────────────────────
const badgesSlice = createSlice({
	name: 'badges',
	initialState,
	reducers: {
		/** Hydrate earned badges from storage. Call once on boot. */
		initializeBadges(state) {
			state.earned = readEarnedBadges();
			state.initialized = true;
		},
		dismissBadgeToast(state, action: PayloadAction<string>) {
			state.toastQueue = state.toastQueue.filter(
				(t) => t.id !== action.payload,
			);
		},
		clearBadgeToasts(state) {
			state.toastQueue = [];
		},
	},
	extraReducers: (builder) => {
		builder.addCase(evaluateBadgesThunk.fulfilled, (state, action) => {
			// Re-read the canonical earned map so the slice matches storage.
			state.earned = readEarnedBadges();

			// The retroactive boot sweep defines the baseline: every id that
			// was provable from durable history at boot. Recorded once, the
			// first time a retroactive eval fulfills. Live evals never alter
			// the baseline — that would defeat its purpose.
			if (action.payload.retroactive && !state.baselineReady) {
				state.bootBaseline = action.payload.earnedIds;
				state.baselineReady = true;
			}

			if (action.payload.toasts.length) {
				state.toastQueue.push(...action.payload.toasts);
			}
		});
	},
});

export const { initializeBadges, dismissBadgeToast, clearBadgeToasts } =
	badgesSlice.actions;
export default badgesSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────
export type WithBadges = { badges: BadgesState };

export const selectEarnedMap = (s: WithBadges) => s.badges.earned;
export const selectBadgeToastQueue = (s: WithBadges) => s.badges.toastQueue;
